import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@common/api";
import { admin, type LogQuery } from "./admin";
import { cloudSaves } from "../emulator/cloudSaves";
import { listCloudRoms } from "./cloudLibrary";
import { getDashboard } from "./dashboard";
import { myPlaytime } from "./playtime";

// The server data the pages show, cached by the query client. The sync
// engine and the emulator hook read the API directly: they want it fresh.
export const queryKeys = {
  library: ["library"] as const,
  dashboard: ["dashboard"] as const,
  playtime: ["playtime"] as const,
  adminMembers: ["admin", "members"] as const,
  adminDeploys: ["admin", "deploys"] as const,
  adminLambdas: ["admin", "lambdas"] as const,
  /** Every player's saves as the admin page shows them; a prefix of adminSaves. */
  adminAllSaves: ["admin", "saves"] as const,
  adminSaves: (sub: string) => ["admin", "saves", sub] as const,
  /** The signed-in account's own cloud saves, as the library page shows them. */
  cloudSaves: ["cloudSaves"] as const,
  adminLogs: (query: LogQuery) => ["admin", "logs", query] as const,
  adminVisitors: ["admin", "visitors"] as const,
};

/** Everything a library change (an upload, a rename, a removal) can alter. */
export const libraryKeys = [queryKeys.library, queryKeys.dashboard, queryKeys.playtime];
/** A rename or removal also moves or drops every player's saves of the game. */
export const romChangeKeys = [...libraryKeys, queryKeys.adminAllSaves, queryKeys.cloudSaves];
/** A change to someone's cloud saves: theirs as the admin page lists them, and the dashboard's last-save times. */
export const saveKeys = (sub: string, mine: boolean) => [queryKeys.adminSaves(sub), queryKeys.dashboard, ...(mine ? [queryKeys.cloudSaves] : [])];

export const errorText = (error: unknown, fallback: string) => (error instanceof ApiError ? error.message : fallback);

export const useCloudRoms = (enabled = true) => useQuery({ queryKey: queryKeys.library, queryFn: listCloudRoms, enabled });
export const useDashboard = (enabled = true) => useQuery({ queryKey: queryKeys.dashboard, queryFn: getDashboard, enabled });
export const useMyPlaytime = (enabled = true) => useQuery({ queryKey: queryKeys.playtime, queryFn: myPlaytime, enabled });
export const useAdminDeploys = (enabled = true) =>
  useQuery({ queryKey: queryKeys.adminDeploys, queryFn: admin.deploys, enabled });
export const useAdminLambdas = (enabled = true) =>
  useQuery({ queryKey: queryKeys.adminLambdas, queryFn: admin.lambdas, enabled });
export const useAdminMembers = (enabled = true) =>
  useQuery({ queryKey: queryKeys.adminMembers, queryFn: admin.members, enabled });
export const useAdminSaves = (sub: string) =>
  useQuery({ queryKey: queryKeys.adminSaves(sub), queryFn: () => admin.saves(sub) });
/** Logs are read when asked for (and on Refresh), never served stale. */
export const useAdminLogs = (query: LogQuery) =>
  useQuery({ queryKey: queryKeys.adminLogs(query), queryFn: () => admin.logs(query), staleTime: 0 });
/** Fresh on every visit (no stale time): they change with every save. */
export const useCloudSaves = (enabled = true) =>
  useQuery({ queryKey: queryKeys.cloudSaves, queryFn: () => cloudSaves.list(), enabled, staleTime: 0 });
export const useAdminVisitors = () => useQuery({ queryKey: queryKeys.adminVisitors, queryFn: admin.visitors });
